import { ALGORITHM_IDS } from '~/models/schemaVersion'

/**
 * The engine's identity, stamped onto every opinion.
 *
 * Never mutated in place. A change in what the engine concludes from the same
 * inputs means a new identifier, so a stored or exported opinion always says
 * which logic produced it (D-027, D-023).
 */
export const RULES_ENGINE_VERSION = ALGORITHM_IDS.rules

/**
 * Individual rule identifiers, recorded on every opinion so a conclusion can be
 * traced back to the rule that produced it.
 *
 * The suffix is the rule's own version. A rule whose meaning changes gets a new
 * number rather than an edit, for the same reason as above.
 */
export const RULE_IDS = {
  contextNoActivePhase: 'context.noActivePhase.v1',
  contextPhaseUnimplemented: 'context.phaseUnimplemented.v1',
  dataInsufficientWeighIns: 'dataQuality.insufficientWeighIns.v1',
  fatLossInRange: 'fatLoss.lossWithinTargetRange.v1',
  fatLossFasterThanRange: 'fatLoss.lossFasterThanTargetRange.v1',
  fatLossStableWeightFallingWaist: 'fatLoss.stableWeightFallingWaist.v1',
  fatLossOffTargetUnjudgeable: 'fatLoss.offTargetWithoutAdherenceEvidence.v1',

  /* Cross-phase families (Checkpoint 2.4). These fire in every phase, which is
     why they are not namespaced to one. */
  recoveryDeteriorating: 'recovery.deteriorating.v1',
  strengthDeclining: 'strength.decliningAcrossLifts.v1',
  adherenceTooLowToJudge: 'adherence.tooLowToJudge.v1',
  phasePlannedEndReached: 'phase.plannedEndReached.v1',

  /* The external authority's configured rate disagrees with the active phase
     (D-096, C6.4). Shows the disagreement; resolves nothing in either system. */
  nutritionSourceConflict: 'nutrition.sourceRateConflict.v1',

  /* Fat loss, completed now that its evidence exists (D-060). */
  fatLossPlateau: 'fatLoss.plateauWithHighAdherence.v1',

  maintenanceStable: 'maintenance.stableInsideRange.v1',
  maintenancePostCutRise: 'maintenance.briefPostCutRise.v1',
  maintenanceSustainedAbove: 'maintenance.sustainedAboveRange.v1',
  maintenanceBelowRange: 'maintenance.driftingBelowRange.v1',

  leanBulkOnTrack: 'leanBulk.slowGainOnTrack.v1',
  leanBulkStalled: 'leanBulk.stalledWithNoProgress.v1',
  leanBulkTooFast: 'leanBulk.gainingTooFast.v1',
  leanBulkLosing: 'leanBulk.losingDuringSurplus.v1',

  miniCutOnTrack: 'miniCut.lossWithinBoundedRange.v1',
  miniCutTooFast: 'miniCut.lossFasterThanBoundedRange.v1',
  miniCutStalled: 'miniCut.notLosingAsPlanned.v1',

  aestheticPrepOnTrack: 'aestheticPrep.onTrackForTheDate.v1',
  aestheticPrepTooFast: 'aestheticPrep.lossFasterThanPlanned.v1',
  aestheticPrepBehind: 'aestheticPrep.behindThePlannedRate.v1',
} as const

export type RuleId = (typeof RULE_IDS)[keyof typeof RULE_IDS]

/**
 * The route engine's identity (D-062), stamped on every recommendation.
 *
 * Separate from `RULES_ENGINE_VERSION` because it is a separate engine: a
 * change to how a week is judged says nothing about how a route is chosen, and
 * a single shared version would make one look like the other had moved.
 */
export const ROUTE_ENGINE_VERSION = ALGORITHM_IDS.route

/**
 * Route identifiers, recorded on every recommendation so a stored snapshot can
 * be traced back to the rule that produced it.
 *
 * Named for the *decision*, not the destination: `buildMuscleRedirectedToFatLoss`
 * and `outcomeRoutedToFatLoss` both end at fat loss, and only the first is a
 * redirect the user must be told about (D-071).
 */
export const ROUTE_RULE_IDS = {
  routedToFatLoss: 'route.fatLoss.v1',
  routedToLeanBulk: 'route.leanBulk.v1',
  routedToMaintenance: 'route.maintenance.v1',
  routedToAestheticPrep: 'route.aestheticPrep.v1',

  /* The two redirects D-071 names, plus the three cells it derives. Each has
     its own identifier because each has its own reason to state. */
  buildMuscleRedirectedToFatLoss: 'route.redirect.buildMuscleAboveFatLossPriority.v1',
  fatLossRedirectedToMaintenance: 'route.redirect.leanEnoughToStopCutting.v1',
  leanBulkFellBackToMaintenance: 'route.fallback.tooFewSessionsForLeanBulk.v1',
  eventTooCloseForPrep: 'route.fallback.eventInsideMinimumLeadTime.v1',
  eventLeanEnoughForPrep: 'route.fallback.leanEnoughForAnEventDeficit.v1',

  unavailableNoWaistToHeight: 'route.unavailable.noWaistToHeight.v1',
} as const

export type RouteRuleId = (typeof ROUTE_RULE_IDS)[keyof typeof ROUTE_RULE_IDS]
